import {
  GetIssueResponseDtoStatusEnum,
  IncomingIssuesDto,
  IssueSubtypesManagedByMeDto,
  IssueTypesManagedByMeDto,
} from '@issuemanager/api-client';
import {
  DEFAULT_FILTER_MODEL_STATE,
  useDataGridContext,
  useIssueStatuses,
} from '@issuemanager/apps/client/components';
import { IncomingIssuesTypeEnum } from '@issuemanager/constants';
import {
  IncomingIssuesEnum,
  QuickFilterAllEnum,
} from '@issuemanager/test/constants';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { QuickFilterContainer } from './quick-filter-container';
import { QuickFilterItem } from './quick-filter-item';

export function IncomingIssues({
  incomingIssues,
  issueTypesManagedByMe,
  issueSubtypesManagedByMe,
  activeFilter,
  setActiveFilter,
}: {
  incomingIssues: IncomingIssuesDto | undefined;
  issueTypesManagedByMe: Array<IssueTypesManagedByMeDto> | undefined;
  issueSubtypesManagedByMe: Array<IssueSubtypesManagedByMeDto> | undefined;
  activeFilter: QuickFilterAllEnum | null;
  setActiveFilter: (value: QuickFilterAllEnum) => void;
}) {
  const context = useDataGridContext();
  const { t } = useTranslation(['issue']);
  const issueStatuses = useIssueStatuses();

  const clearFilters = () => {
    context.setFilterModel(DEFAULT_FILTER_MODEL_STATE);
  };

  const statusOpen = useMemo(
    () =>
      issueStatuses.find((s) => s.value === GetIssueResponseDtoStatusEnum.Open),
    [issueStatuses],
  );

  const issueTypes = useMemo(
    () =>
      issueTypesManagedByMe?.map((issueType) => ({
        id: issueType.id,
        name: issueType.name,
      })),
    [issueTypesManagedByMe],
  );

  const issueSubtypes = useMemo(
    () =>
      issueSubtypesManagedByMe?.map((issueSubtype) => ({
        id: issueSubtype.id,
        name: issueSubtype.name,
      })),
    [issueSubtypesManagedByMe],
  );

  const handleFilterSelection = (type: string) => {
    clearFilters();
    switch (type) {
      case IncomingIssuesTypeEnum.ALL:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          issueType: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            issueType: issueTypes,
            issueSubtype: issueSubtypes,
          }),
          'quick-filter',
        );
        break;
      case IncomingIssuesTypeEnum.UNASSIGNED:
        context.setColumnVisibilityModel((prevState) => ({
          ...prevState,
          issueType: true,
          status: true,
        }));
        context.setFilterModel(
          (prevState) => ({
            ...prevState,
            issueType: issueTypes,
            issueSubtype: issueSubtypes,
            status: [statusOpen],
          }),
          'quick-filter',
        );
        break;
    }
  };

  return (
    <QuickFilterContainer label={t('quickFilters.incomingIssues.title')}>
      <QuickFilterItem
        label={t('quickFilters.incomingIssues.all')}
        number={incomingIssues?.all}
        onClick={() => {
          handleFilterSelection(IncomingIssuesTypeEnum.ALL);
          setActiveFilter(IncomingIssuesEnum.ALL);
        }}
        isActive={activeFilter === IncomingIssuesEnum.ALL}
      />
      <QuickFilterItem
        label={t('quickFilters.incomingIssues.unassigned')}
        number={incomingIssues?.unAssigned}
        onClick={() => {
          handleFilterSelection(IncomingIssuesTypeEnum.UNASSIGNED);
          setActiveFilter(IncomingIssuesEnum.UNASSIGNED);
        }}
        isActive={activeFilter === IncomingIssuesEnum.UNASSIGNED}
      />
    </QuickFilterContainer>
  );
}
